import React, { useMemo, useRef, useEffect, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import CartLoader from "../../Helper/Loaders/CartLoader";
import { useBaseEntityInfo } from "../../Hooks/usePagesContent";
import { buildSOHBuckets } from "../../PagesContent/BatteryIntelligence/SOHAndRUL/utils/sohBuckets";

// ─── Layout constants (matches BreakdownChart theme) ──────────────────────────
const SCROLLBAR_WIDTH  = "8px";
const CHART_COLOR      = "#7EC862";

// ─── Default SOH label colors: the shared fallback buckets (callers normally pass labelColorMap) ──
const SOH_LABEL_COLOR_MAP = Object.fromEntries(buildSOHBuckets(undefined).map((b) => [b.name, b.color]));
const FIXED_BAR_HEIGHT = 16;
const BAR_GAP          = 30;
const LABEL_HEIGHT     = FIXED_BAR_HEIGHT + BAR_GAP; // 46 px per row
const LABEL_WIDTH      = 180;                         // px — matches BreakdownChart
const X_MAX            = 100;
const X_TICKS          = [0, 25, 50, 75, 100];
const TOOLTIP_GAP_X    = 2;
const TOOLTIP_GAP_Y    = 2;
const DATA_LABEL_GAP_X = 8;
const CHART_RIGHT_PAD  = 56;

// ─── Styled containers (mirrors BreakdownChart exactly) ───────────────────────
// No overflow clipping — allows the ApexCharts tooltip to render above row 1
// without being cut off. (Only 7 fixed rows, so scrolling is not needed.)
const ChartContainer = styled(Box)({
  width: "100%",
  display: "flex",
  gap: "16px",
  overflow: "visible",
});

const LabelsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: `${LABEL_WIDTH}px`,
  flexShrink: 0,
});

const ChartWrapper = styled(Box)({
  flex: 1,
  minWidth: 0,
  position: "relative",
  "& .apexcharts-canvas svg": { cursor: "pointer" },
});

// ─── Component ─────────────────────────────────────────────────────────────────
const SOHBreakdownBarChart = ({ chartRows = [], loading, onBarClick, endDate, chartColor = CHART_COLOR, labelColorMap, yAxisLabel }) => {
  const { baseEntityTypePluralLable } = useBaseEntityInfo();
  const hoveredIndexRef = useRef(null);
  const chartRowsRef    = useRef(chartRows);
  useEffect(() => { chartRowsRef.current = chartRows; }, [chartRows]);

  const setTooltipNearCursor = useCallback((tooltip, relX, relY) => {
    const top = `${relY + TOOLTIP_GAP_Y}px`;
    const left = `${relX + TOOLTIP_GAP_X}px`;
    tooltip.style.setProperty("top", top, "important");
    tooltip.style.setProperty("left", left, "important");
    tooltip.style.setProperty("right", "auto", "important");
    tooltip.style.setProperty("bottom", "auto", "important");
    tooltip.style.setProperty("transform", "none", "important");
  }, []);

  // r.category is the bucket's API value (colour / click lookups); r.displayLabel, when
  // set, is the text shown for it (e.g. an org label for "SOH Unavailable").
  const categories  = useMemo(() => chartRows.map((r) => r.category), [chartRows]);
  const rowLabels   = useMemo(() => chartRows.map((r) => r.displayLabel ?? r.category), [chartRows]);
  const colorMap    = labelColorMap ?? SOH_LABEL_COLOR_MAP;
  const barColors   = useMemo(() => chartRows.map((r) => colorMap[r.category] ?? chartColor), [chartRows, colorMap, chartColor]);
  // Apply a minimum visual bar width of 2% so tiny values (e.g. 0.16%) are still visible.
  // The real percentage is still shown in the overlay label and tooltip.
  const MIN_BAR_WIDTH = 2;
  const series      = useMemo(() => [
    {
      name: "Percentage of Fleet",
      data: chartRows.map((r) => (r.percentage > 0 ? Math.max(r.percentage, MIN_BAR_WIDTH) : 0)),
    },
  ], [chartRows]);
  const formattedEndDate = useMemo(() => {
    if (!endDate) return "";

    // Handle YYYY-MM-DD safely without timezone shift.
    const ymdMatch = String(endDate).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdMatch) {
      const [, y, m, d] = ymdMatch;
      const monthShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthIdx = Number(m) - 1;
      if (monthIdx >= 0 && monthIdx < 12) return `${d} ${monthShort[monthIdx]} ${y}`;
    }

    const parsed = new Date(endDate);
    if (Number.isNaN(parsed.getTime())) return String(endDate);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(parsed);
  }, [endDate]);

  const chartHeight = categories.length * LABEL_HEIGHT;

  // ─── Row hover highlight (same SVG-rect approach as BreakdownChart) ─────────
  const addHoverBackground = useCallback((index, chartContext) => {
    // Clear any existing highlight rects
    document.querySelectorAll(".soh-hover-row-bg").forEach((el) => el.remove());
    if (index === null || index === undefined || index < 0) return;

    if (chartContext) {
      const innerChart = chartContext.el?.querySelector(".apexcharts-inner");
      if (innerChart) {
        const chartWidth  = innerChart.getBBox().width;
        const hoverHeight = FIXED_BAR_HEIGHT + 8;
        const offset      = (LABEL_HEIGHT - hoverHeight) / 2;
        const yPos        = index * LABEL_HEIGHT + offset;

        const svgNS = "http://www.w3.org/2000/svg";
        const rect  = document.createElementNS(svgNS, "rect");
        rect.setAttribute("class",  "soh-hover-row-bg");
        rect.setAttribute("x",      "0");
        rect.setAttribute("y",      String(yPos));
        rect.setAttribute("width",  String(chartWidth));
        rect.setAttribute("height", String(hoverHeight));
        rect.setAttribute("fill",   "#E9E9E9");
        innerChart.insertBefore(rect, innerChart.firstChild);
      }
    }
  }, []);

  const removeHoverBackground = useCallback(() => {
    document.querySelectorAll(".soh-hover-row-bg").forEach((el) => el.remove());
  }, []);

  // ─── Bar click handler ───────────────────────────────────────────────────────
  const handleDataPointSelection = useCallback((_event, _ctx, config) => {
    const idx = config?.dataPointIndex;
    if (idx === undefined || idx < 0) return;
    const row = chartRowsRef.current[idx];
    if (row) onBarClick?.(row);
  }, [onBarClick]);

  // ─── ApexCharts options ──────────────────────────────────────────────────────
  const options = useMemo(() => ({
    colors: barColors,
    chart: {
      type:               "bar",
      height:             chartHeight,
      sparkline:          { enabled: true },
      parentHeightOffset: 0,
      toolbar:            { show: false },
      animations:         { enabled: false },
      events: {
        mouseMove: (event, chartContext, config) => {
          // Row hover highlight
          const idx = config.dataPointIndex;
          if (idx >= 0 && idx !== hoveredIndexRef.current) {
            hoveredIndexRef.current = idx;
            addHoverBackground(idx, chartContext);
          }

          // Anchor tooltip's top-left just beside the mouse pointer
          requestAnimationFrame(() => {
            const tooltip = chartContext.el?.querySelector(".apexcharts-tooltip");
            if (!tooltip?.classList.contains("apexcharts-active")) return;
            const containerRect = chartContext.el.getBoundingClientRect();
            const relX = event.clientX - containerRect.left;
            const relY = event.clientY - containerRect.top;
            setTooltipNearCursor(tooltip, relX, relY);

            // Apex may re-position once again after hover settles.
            // Re-apply once more shortly so tooltip doesn't jump to bar end.
            setTimeout(() => {
              if (tooltip.classList.contains("apexcharts-active")) {
                setTooltipNearCursor(tooltip, relX, relY);
              }
            }, 40);
          });
        },
        mouseLeave: () => {
          hoveredIndexRef.current = null;
          removeHoverBackground();
        },
        dataPointSelection: handleDataPointSelection,
      },
    },
    plotOptions: {
      bar: {
        horizontal:              true,
        distributed:             true,
        borderRadius:            4,
        borderRadiusApplication: "end",
        barHeight:               `${FIXED_BAR_HEIGHT}px`,
        dataLabels: {
          // Keep percentage labels outside, after bar end
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories,
      min:        0,
      max:        X_MAX,
      labels:     { show: false },
      axisBorder: { show: false },
      axisTicks:  { show: false },
    },
    yaxis:  { show: false },
    grid: {
      show: true,
      padding: { top: 0, bottom: 0, left: 0, right: CHART_RIGHT_PAD },
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: false } },
    },
    legend: { show: false },
    states: { active: { filter: { type: "none" } } },
    // ── Tooltip ───────────────────────────────────────────────────────────────
    tooltip: {
      followCursor: true,
      custom: ({ dataPointIndex }) => {
        const row = chartRowsRef.current[dataPointIndex];
        if (!row) return "";
        const dateLabel = formattedEndDate;
        const dotColor  = colorMap[row.category] ?? chartColor;
        return `
          <div style="
            background:#fff;
            border:1px solid #E3E3E3;
            border-radius:6px;
            padding:0;
            width:170px;
            box-shadow:0 2px 8px rgba(0,0,0,0.18);
            font-family:inherit;
            overflow:hidden;
          ">
            ${dateLabel
              ? `<div style="font-size:13px;font-weight:700;color:#1F1F1F;background:#EEF0F4;padding:6px 10px">${dateLabel}</div>`
              : ""}
            <div style="padding:10px">
              <div style="display:flex;align-items:center;gap:8px">
                <span style="width:9px;height:9px;border-radius:50%;background:${dotColor};display:inline-block;flex-shrink:0"></span>
                <span style="font-size:12px;color:#4A4A4A">
                  Number of ${baseEntityTypePluralLable}:&nbsp;<strong style="color:#1F1F1F">${row.count}</strong>
                </span>
              </div>
              <div style="font-size:12px;color:#4A4A4A;padding-top:8px">
                Percentage of Fleet:&nbsp;<strong style="color:#1F1F1F">${row.percentage}%</strong>
              </div>
            </div>
          </div>
        `;
      },
    },
  }), [categories, chartHeight, barColors, colorMap, addHoverBackground, removeHoverBackground, handleDataPointSelection, formattedEndDate, setTooltipNearCursor, chartColor]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CartLoader />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>

      {/* ── Column header ───────────────────────────────────────────────────── */}
      {yAxisLabel && (
        <Box sx={{ display: "flex", gap: "16px", mb: "6px" }}>
          <Box sx={{ width: `${LABEL_WIDTH}px`, flexShrink: 0 }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#8F8F8F",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              {yAxisLabel}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
        </Box>
      )}

      {/* ── Labels + Chart row ──────────────────────────────────────────────── */}
      <ChartContainer>

        {/* Left: custom category labels */}
        <LabelsContainer
          onMouseLeave={() => {
            hoveredIndexRef.current = null;
            removeHoverBackground();
          }}
        >
          {rowLabels.map((label, index) => (
            <Box
              key={index}
              data-testid={`breakdown-bar-label-${categories[index]}`}
              onMouseEnter={() => {
                if (hoveredIndexRef.current !== index) {
                  hoveredIndexRef.current = index;
                  addHoverBackground(index, null);
                }
              }}
              sx={{
                height: `${LABEL_HEIGHT}px`,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  height: `${FIXED_BAR_HEIGHT + 8}px`,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "4px",
                  transition: "background-color 0.1s ease",
                }}
              >
                <PrimaryTooltip title={label} placement="bottom">
                  <Typography
                    sx={{
                      fontSize:     "14px",
                      lineHeight:   "16px",
                      color:        "#494949",
                      overflow:     "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace:   "nowrap",
                      width:        "100%",
                      cursor:       "pointer",
                    }}
                  >
                    {label}
                  </Typography>
                </PrimaryTooltip>
              </Box>
            </Box>
          ))}
        </LabelsContainer>

        {/* Right: ApexCharts sparkline bar */}
        <ChartWrapper>
          <ReactApexChart
            type="bar"
            series={series}
            options={options}
            height={chartHeight}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          >
            {chartRows.map((row, index) => {
              const displayedPct = row.percentage > 0 ? Math.max(row.percentage, MIN_BAR_WIDTH) : 0;
              return (
              <Box
                key={`${row.category}-label`}
                sx={{
                  position: "absolute",
                  top: `${index * LABEL_HEIGHT}px`,
                  left: `calc(${displayedPct}% - ${(displayedPct / X_MAX) * CHART_RIGHT_PAD}px + ${DATA_LABEL_GAP_X}px)`,
                  height: `${LABEL_HEIGHT}px`,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    lineHeight: "16px",
                    fontWeight: 400,
                    color: "#494949",
                    whiteSpace: "nowrap",
                  }}
                >
                  {`${row.percentage}%`}
                </Typography>
              </Box>
              );
            })}
          </Box>
        </ChartWrapper>

      </ChartContainer>

      {/* ── Custom x-axis ticks: 0, 25, 50, 75, 100 ────────────────────────── */}
      <Box sx={{ display: "flex", gap: "16px", width: "100%", mt: "8px" }}>
        {/* Spacer matching LabelsContainer */}
        <Box sx={{ width: `${LABEL_WIDTH}px`, flexShrink: 0 }} />

        {/* Tick area */}
        <Box sx={{ flex: 1, position: "relative", height: "30px", borderTop: "1px solid #E0E0E0" }}>
          {X_TICKS.map((tick, index) => {
            const isFirst = index === 0;
            const isLast  = index === X_TICKS.length - 1;
            const transform = isLast
              ? "translateX(-100%)"
              : isFirst
              ? "translateX(0%)"
              : "translateX(-50%)";
            return (
              <Box
                key={tick}
                sx={{
                  position:  "absolute",
                  left:      `calc(${(tick / X_MAX) * 100}% - ${(tick / X_MAX) * CHART_RIGHT_PAD}px)`,
                  transform,
                  top:       "4px",
                }}
              >
                <Typography sx={{ fontSize: "12px", lineHeight: "24px", color: "#8F8F8F", whiteSpace: "nowrap" }}>
                  {tick}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── X-axis title ────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: "4px", mb: "8px" }}>
        <Typography sx={{ fontSize: "12px", color: "#8F8F8F" }}>
          Percentage of Fleet
        </Typography>
      </Box>

    </Box>
  );
};

export default SOHBreakdownBarChart;
